import bodyParts from "@/store/survey/body-parts"
import defaultAnswers from "@/store/survey/answers"
import sections from "@/store/survey/questions"
import {apiRoutes} from "@/router";

/*
|--------------------------------------------------------------------------
| State
|--------------------------------------------------------------------------
*/


const state = {
  currentStep: null,

  totalSteps: 5,

  surveyComplete: false,

  needsAppointment: null,

  answers: JSON.parse(JSON.stringify(defaultAnswers)),

  bodyParts,

  sections,

  postingTriageData: false,
};

/*
|--------------------------------------------------------------------------
| Helpers
|--------------------------------------------------------------------------
*/

/**
 * Pick out the relevant question depending on the body part.
 *
 * @param {string} bodyPart
 * @param {array} conditions
 * @returns {object|undefined}
 */
const getRelevantQuestion = (bodyPart, conditions) => {
  let i, question;

  for (i in conditions) {
    if (conditions.hasOwnProperty(i)) {
      question = conditions[i];

      if (question.only === undefined && question.not === undefined) {
        throw new Error('Conditional questions must define a "not" or "only" key with an array of body parts');
      } else if (question.only && question.not) {
        throw new Error('Conditional questions must define either a "not" OR "only" key with an array of body parts (both defined)');
      }

      if (question.only && window._.includes(question.only, bodyPart)) {
        return question;
      }

      if (question.not && !window._.includes(question.not, bodyPart)) {
        return question;
      }
    }
  }
};

/**
 * Build the triage payload ready for sending to the API.
 *
 * @param {string} key
 * @param {object} state
 * @returns {object}
 */
export const buildTriagePayload = (state) => {
  let payload = {
    bodyPart: state.answers.bodyPart,
    isComplete: false,
    outcome: null,
    answers: {},
  };

  const getSectionQuestions = getters.getSectionQuestions(state);

  let sectionIndex,
      questionIndex,
      questions,
      question,
      formattedQuestions,
      formattedQuestion,
      answer,
      totalQuestions = 0,
      answeredQuestions = 0,
      answerIndex,
      needsAppointment = false,
      skipToAppointment = false
  ;


  for (sectionIndex = 1; sectionIndex <= Object.keys(state.sections).length; sectionIndex++) {
    questions = getSectionQuestions(sectionIndex);

    formattedQuestions = [];

    for (questionIndex in questions) {
      if (questions.hasOwnProperty(questionIndex)) {
        question = questions[questionIndex];
        answer = state.answers[`section${sectionIndex}`][questionIndex];

        totalQuestions++;

        const hasbeenSkipped = question.skipQuestion && question.skipQuestion(state);

        if ((typeof answer !== 'undefined' && answer !== null) || hasbeenSkipped) {
          answeredQuestions++;

          if (!skipToAppointment && question.goStraightToAppointment && question.goStraightToAppointment(answer)) {
            skipToAppointment = true;
          }

          if (!needsAppointment && question.needsAppointment && question.needsAppointment(answer)) {
            needsAppointment = true;
          }

          // Convert checkbox inter arrays to text arrays.
          if (question.answers === "checkboxes" && answer && typeof (answer.length) === "number") {
            answer = JSON.parse(JSON.stringify(answer));

            for (answerIndex in answer) {
              if (answer.hasOwnProperty(answerIndex)) {
                answer[answerIndex] = {
                  question: question.details[answer[answerIndex]],
                  answer: true,
                };
              }
            }
          }
        }

        formattedQuestion = {};
        formattedQuestion.id = question.id;
        formattedQuestion.question = window.Arr.wrap(question.title).join("\n");
        formattedQuestion.options = question.details || [];
        formattedQuestion.answers = question.answers || {
          Yes: true,
          No: false,
        };
        formattedQuestion.answer = answer;
      }

      formattedQuestions.push(formattedQuestion);
    }

    payload.answers[`section${sectionIndex}`] = formattedQuestions;
  }

  payload.isComplete = skipToAppointment || answeredQuestions === totalQuestions;
  payload.outcome = payload.isComplete ? (needsAppointment ? "appointment" : "self-manage") : null;

  return payload;
};

/*
|--------------------------------------------------------------------------
| Getters
|--------------------------------------------------------------------------
*/

const getters = {
  getSectionQuestions: (state) => (section) => {
    const allQuestions = state.sections[`section${section}`];

    let i, question, questions = {};

    for (i in allQuestions) {
      if (allQuestions.hasOwnProperty(i)) {
        question = allQuestions[i];

        if (question.conditions === undefined) {
          questions[i] = question;
        } else {
          question = getRelevantQuestion(state.answers.bodyPart, question.conditions);

          if (question) {
            questions[i] = question;
          }
        }
      }
    }

    return questions;
  },
};

/*
|--------------------------------------------------------------------------
| Actions
|--------------------------------------------------------------------------
*/

const actions = {
  setCurrentStep({commit}, step) {
    commit("mutateCurrentStep", step);
  },

  setTotalSteps({commit}, total) {
    commit("mutateTotalSteps", total);
  },

  setSurveyComplete({commit}, complete) {
    commit("mutateSurveyComplete", complete);
  },

  setNeedsAppointment({commit}, needsAppointment) {
    commit("mutateNeedsAppointment", needsAppointment);
  },

  setAnswer({commit}, {section, question, answer}) {
    commit("mutateAnswer", {section, question, answer});
  },

  setBodyPart({commit}, bodyPart) {
    commit("mutateBodyPart", bodyPart);
  },

  clearAnswers({commit}) {
    commit("resetAnswers");
  },

  postTriageState({commit, state}, {payload, onSuccess, onError}) {
    if (state.postingTriageData) {
      return;
    }

    const successCallback = response => {
      commit("mutatePostingTriageData", false);

      window.Events.dispatch("triage.sent");

      document.cookie = `DigitalTriageKey=${response.data.clientKey};domain=${process.env.VUE_APP_PARENT_DOMAIN};max-age=7890000`;

      onSuccess && onSuccess(response);
    };

    const errorCallback = error => {
      commit("mutatePostingTriageData", false);

      window.Events.dispatch("triage.sent");

      onError && onError(error);
    };

    commit("mutatePostingTriageData", true);

    window.Events.dispatch("triage.sending");

    delete payload.isComplete;

    window.axios.post(apiRoutes.patientTriage, payload).then(successCallback).catch(errorCallback);
  },
};

/*
|--------------------------------------------------------------------------
| Mutations
|--------------------------------------------------------------------------
*/

const mutations = {
  mutateCurrentStep(state, step) {
    state.currentStep = step;
  },

  mutateTotalSteps(state, total) {
    state.totalSteps = total;
  },

  mutateSurveyComplete(state, complete) {
    state.surveyComplete = complete;
  },

  mutateNeedsAppointment(state, needsAppointment) {
    state.needsAppointment = needsAppointment;
  },

  mutateAnswer(state, {section, question, answer}) {
    state.answers[`section${section}`][`question${question}`] = answer;
  },

  mutateBodyPart(state, bodyPart) {
    state.answers.bodyPart = bodyPart;
  },

  resetAnswers(state) {
    state.answers = JSON.parse(JSON.stringify(defaultAnswers));
  },

  mutatePostingTriageData(state, posting) {
    state.postingTriageData = posting;
  },
};

/*
|--------------------------------------------------------------------------
| Export
|--------------------------------------------------------------------------
*/

export const survey = {
  namespaced: true,
  actions,
  getters,
  mutations,
  state,
};
